import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Calendar,
  Filter,
  Edit,
  Trash2,
  Plus,
  X,
  Save,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend,
  Area,
  AreaChart,
} from "recharts";

const Finanzas = () => {
  const { user } = useAuth();
  const [financeData, setFinanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("6m");
  const [showModal, setShowModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [newBudget, setNewBudget] = useState({
    name: "",
    budget: 0,
    spent: 0,
  });

  const isSuperAdmin = user?.is_super_admin || user?.role === "admin";

  useEffect(() => {
    fetchFinanceData();
  }, []);

  const fetchFinanceData = async () => {
    try {
      const response = await axios.get(`${API}/dashboard/finance`, {
        withCredentials: true,
      });
      setFinanceData(response.data);
    } catch (error) {
      console.error("Error fetching finance data:", error);
      // Initialize with empty structure for production
      setFinanceData({
        total_budget: 0,
        spent: 0,
        remaining: 0,
        by_direction: [],
        monthly_trend: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const recalculateTotals = (directions) => {
    const spent = directions.reduce((sum, d) => sum + d.spent, 0);
    const total = directions.reduce((sum, d) => sum + d.budget, 0);
    return { total_budget: total, spent, remaining: total - spent };
  };

  const handleEditBudget = (direction) => {
    setEditingBudget(direction);
    setNewBudget({
      name: direction.name,
      budget: direction.budget,
      spent: direction.spent,
    });
    setShowModal(true);
  };

  const handleSaveBudget = () => {
    if (editingBudget) {
      // Update existing
      const updatedDirections = financeData.by_direction.map(d =>
        d.id === editingBudget.id ? { ...d, ...newBudget } : d
      );
      const totals = recalculateTotals(updatedDirections);
      setFinanceData({ ...financeData, by_direction: updatedDirections, ...totals });
    } else {
      // Add new
      const newDirection = {
        id: `dir_${Date.now()}`,
        ...newBudget,
      };
      const updatedDirections = [...financeData.by_direction, newDirection];
      const totals = recalculateTotals(updatedDirections);
      setFinanceData({ ...financeData, by_direction: updatedDirections, ...totals });
    }
    setShowModal(false);
    setEditingBudget(null);
    setNewBudget({ name: "", budget: 0, spent: 0 });
  };

  const handleDeleteBudget = async (directionId) => {
    if (!window.confirm("¿Estás seguro de eliminar este presupuesto?")) return;
    try {
      await axios.delete(`${API}/budgets/${directionId}`, {
        withCredentials: true,
      });
      // Refresh data from backend
      await fetchFinanceData();
    } catch (error) {
      console.error("Error deleting budget:", error);
      // Fallback to local state update if API fails
      const updatedDirections = financeData.by_direction.filter(d => d.id !== directionId);
      const totals = recalculateTotals(updatedDirections);
      setFinanceData({ ...financeData, by_direction: updatedDirections, ...totals });
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
      minimumFractionDigits: 0,
    }).format(value);
  };

  const pieColors = ["#6C8ECC", "#9EC6EE", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899"];

  const budgetUsageData = financeData?.by_direction?.map((item, index) => ({
    name: item.name,
    value: item.spent,
    color: pieColors[index % pieColors.length],
  })) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finanzas">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Finanzas</h1>
          <p className="text-sm sm:text-base text-slate-500">Dashboard de presupuestos y proyecciones</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="glass-input flex-1 sm:flex-none"
          >
            <option value="3m">Últimos 3 meses</option>
            <option value="6m">Últimos 6 meses</option>
            <option value="1y">Último año</option>
          </select>
          <button className="btn-secondary flex items-center gap-2">
            <Download size={18} />
            <span className="hidden sm:inline">Exportar</span>
          </button>
          {isSuperAdmin && (
            <button
              onClick={() => {
                setEditingBudget(null);
                setNewBudget({ name: "", budget: 0, spent: 0 });
                setShowModal(true);
              }}
              className="btn-primary flex items-center gap-2"
              data-testid="add-budget-btn"
            >
              <Plus size={18} />
              <span className="hidden sm:inline">Agregar</span>
            </button>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        <div className="glass-panel p-4 sm:p-6" data-testid="budget-total">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
              <DollarSign size={20} className="text-primary sm:w-6 sm:h-6" />
            </div>
            <span className="badge badge-primary">Total</span>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-slate-800">
            {formatCurrency(financeData?.total_budget)}
          </p>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Presupuesto Total</p>
        </div>

        <div className="glass-panel p-4 sm:p-6" data-testid="budget-spent">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-amber-100 flex items-center justify-center">
              <TrendingDown size={20} className="text-amber-600 sm:w-6 sm:h-6" />
            </div>
            <div className="flex items-center gap-1 text-amber-600">
              <ArrowDownRight size={16} />
              <span className="text-sm font-medium">
                {financeData?.total_budget > 0 ? Math.round((financeData?.spent / financeData?.total_budget) * 100) : 0}%
              </span>
            </div>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-slate-800">
            {formatCurrency(financeData?.spent)}
          </p>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Gastado</p>
        </div>

        <div className="glass-panel p-4 sm:p-6" data-testid="budget-remaining">
          <div className="flex items-center justify-between mb-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-emerald-100 flex items-center justify-center">
              <TrendingUp size={20} className="text-emerald-600 sm:w-6 sm:h-6" />
            </div>
            <div className="flex items-center gap-1 text-emerald-600">
              <ArrowUpRight size={16} />
              <span className="text-sm font-medium">
                {financeData?.total_budget > 0 ? Math.round((financeData?.remaining / financeData?.total_budget) * 100) : 0}%
              </span>
            </div>
          </div>
          <p className="text-2xl sm:text-3xl font-bold text-slate-800">
            {formatCurrency(financeData?.remaining)}
          </p>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">Disponible</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Income vs Expenses Trend */}
        <div className="glass-panel p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-slate-800 mb-4 sm:mb-6">
            Tendencia Ingresos vs Gastos
          </h2>
          <div className="h-64 sm:h-80" style={{ minWidth: 0 }}>
            {financeData?.monthly_trend && financeData.monthly_trend.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={financeData.monthly_trend}>
                  <defs>
                    <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6C8ECC" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#6C8ECC" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 12 }} />
                  <YAxis tick={{ fill: "#64748B", fontSize: 12 }} tickFormatter={(v) => `$${v/1000}k`} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "none",
                      borderRadius: "12px",
                      boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
                    }}
                    formatter={(value) => [formatCurrency(value), ""]}
                  />
                  <Area
                    type="monotone"
                    dataKey="income"
                    stroke="#10B981"
                    strokeWidth={2}
                    fill="url(#incomeGradient)"
                    name="Ingresos"
                  />
                  <Area
                    type="monotone"
                    dataKey="expenses"
                    stroke="#6C8ECC"
                    strokeWidth={2}
                    fill="url(#expenseGradient)"
                    name="Gastos"
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-slate-500">No hay datos disponibles</p>
              </div>
            )}
          </div>
        </div>

        {/* Budget Distribution */}
        <div className="glass-panel p-4 sm:p-6">
          <h2 className="text-base sm:text-lg font-semibold text-slate-800 mb-4 sm:mb-6">
            Distribución por Dirección
          </h2>
          <div className="h-64 sm:h-80" style={{ minWidth: 0 }}>
            {budgetUsageData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={budgetUsageData}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {budgetUsageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "none",
                      borderRadius: "12px",
                      boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
                    }}
                    formatter={(value) => [formatCurrency(value), "Gastado"]}
                  />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full">
                <p className="text-slate-500">No hay datos disponibles</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Budget by Direction Details */}
      <div className="glass-panel p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-slate-800">
            Detalle por Dirección
          </h2>
          {isSuperAdmin && (
            <button
              onClick={() => {
                setEditingBudget(null);
                setNewBudget({ name: "", budget: 0, spent: 0 });
                setShowModal(true);
              }}
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <Plus size={16} />
              Agregar
            </button>
          )}
        </div>
        <div className="space-y-4">
          {financeData?.by_direction && financeData.by_direction.length > 0 ? (
            financeData.by_direction.map((item, index) => {
            const percentage = Math.round((item.spent / item.budget) * 100);
            return (
              <div key={item.id || index} className="p-4 rounded-xl bg-slate-50/50 hover:bg-slate-100/50 transition-colors" data-testid={`direction-budget-${index}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-slate-700">{item.name}</span>
                  <div className="flex items-center gap-2 sm:gap-4">
                    <span className="text-xs sm:text-sm text-slate-500 hidden sm:block">
                      {formatCurrency(item.spent)} / {formatCurrency(item.budget)}
                    </span>
                    <span className={`text-sm font-medium ${
                      percentage > 90 ? "text-rose-600" :
                      percentage > 70 ? "text-amber-600" :
                      "text-emerald-600"
                    }`}>
                      {percentage}%
                    </span>
                    {isSuperAdmin && (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEditBudget(item)}
                          className="p-1.5 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                          title="Editar"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteBudget(item.id)}
                          className="p-1.5 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="text-xs text-slate-500 mb-2 sm:hidden">
                  {formatCurrency(item.spent)} / {formatCurrency(item.budget)}
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${
                      percentage > 90 ? "bg-rose-500" :
                      percentage > 70 ? "bg-amber-500" :
                      "bg-emerald-500"
                    }`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  ></div>
                </div>
              </div>
            );
            })
          ) : (
            <div className="text-center py-8 text-slate-400">
              <p>No hay presupuestos registrados</p>
            </div>
          )}
        </div>
      </div>

      {/* Projections */}
      <div className="glass-panel p-4 sm:p-6 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <PieChart size={20} className="text-primary sm:w-6 sm:h-6" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-semibold text-slate-800 mb-2">
              Proyección para fin de año
            </h2>
            <p className="text-sm sm:text-base text-slate-600">
              {financeData?.remaining && financeData.remaining > 0 ? (
                <>
                  Basado en el ritmo actual de gastos, se estima que el presupuesto restante de{" "}
                  <span className="font-semibold text-primary">
                    {formatCurrency(financeData.remaining)}
                  </span>{" "}
                  será suficiente para cubrir las operaciones.
                </>
              ) : (
                "No hay datos de proyección disponibles. Ingresa información financiera para ver proyecciones."
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Add/Edit Budget Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-6 sm:p-8 w-full max-w-md animate-slide-up" data-testid="budget-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">
                {editingBudget ? "Editar Presupuesto" : "Nuevo Presupuesto"}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingBudget(null);
                }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Dirección / Área
                </label>
                <input
                  type="text"
                  value={newBudget.name}
                  onChange={(e) => setNewBudget({ ...newBudget, name: e.target.value })}
                  className="w-full glass-input"
                  placeholder="Nombre de la dirección"
                  data-testid="budget-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Presupuesto Asignado (MXN)
                </label>
                <input
                  type="number"
                  value={newBudget.budget}
                  onChange={(e) => setNewBudget({ ...newBudget, budget: parseInt(e.target.value) || 0 })}
                  className="w-full glass-input"
                  placeholder="100000"
                  data-testid="budget-amount-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Monto Gastado (MXN)
                </label>
                <input
                  type="number"
                  value={newBudget.spent}
                  onChange={(e) => setNewBudget({ ...newBudget, spent: parseInt(e.target.value) || 0 })}
                  className="w-full glass-input"
                  placeholder="50000"
                  data-testid="budget-spent-input"
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingBudget(null);
                  }}
                  className="btn-secondary order-2 sm:order-1"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveBudget}
                  disabled={!newBudget.name.trim()}
                  className="btn-primary flex items-center justify-center gap-2 order-1 sm:order-2"
                  data-testid="save-budget-btn"
                >
                  <Save size={18} />
                  {editingBudget ? "Guardar Cambios" : "Crear Presupuesto"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Finanzas;
